import {type MoneyResponseBody} from '@online-offer/shared';

import {isNil, isNotEmpty, isNotNil} from 'ramda';

import {FinancialProductOffer} from '../../api/types/onlineOfferTypes';
import {
  getLeasingCostLabelKeys,
  LeasingConditionalCostField,
  LeasingCostId,
} from './getLeasingCostLabelKeys';

export type LeasingCostItem = {
  id: LeasingCostId;
  labelKey: string;
  /** Null where the item's tooltip would not describe it, e.g. a paid-once item. */
  tooltipKey: string | null;
  amount: MoneyResponseBody;
  unit: 'PER_MONTH' | 'PER_KILOMETRE' | null;
  /** Per-kilometre amounts keep their cents; every other amount is shown in whole units. */
  decimals: number;
};

export type LeasingCostSections = {
  /** Null at 0, so a leasing without a down payment never shows one. */
  downPayment: MoneyResponseBody | null;
  /** Null when the offer has no monthly services, so no "vehicle + services" line is shown. */
  monthlyServicesTotal: MoneyResponseBody | null;
  paidOnce: LeasingCostItem[];
  conditional: LeasingCostItem[];
  /** Null without published costs (an older backend, a legacy contract): no includes, no total. */
  includes: LeasingCostItem[] | null;
};

const CONDITIONAL_COST_FIELDS: LeasingConditionalCostField[] = [
  'excessMileageCost',
  'underMileageCredit',
  'liabilityInsuranceDeductible',
  'comprehensiveInsuranceDeductible',
];
const PER_KILOMETRE_FIELDS: LeasingConditionalCostField[] = [
  'excessMileageCost',
  'underMileageCredit',
];
// The registration tooltip describes the paid-once case, so only these explain an included item.
const INCLUDED_ITEMS_WITH_TOOLTIP: LeasingCostId[] = ['VEHICLE_TRANSFER', 'VEHICLE_DELIVERY'];
const DECIMAL_SEPARATOR = '.';

const getAmountScale = (money: MoneyResponseBody) =>
  money.amount.split(DECIMAL_SEPARATOR).at(1)?.length ?? 0;

/**
 * Sorts what a leasing offer costs besides its rate into what the customer sees: the down payment,
 * what is paid once on top, what is charged only when it happens, and what the monthly payment
 * includes. Everything depends on the offer alone, never on the quoted coordinates, so the screen
 * keeps its height while the customer moves the controls, and print lists the same items.
 */
export const getLeasingCostSections = (offer: FinancialProductOffer): LeasingCostSections => {
  const costs = offer.leasingCosts;
  const downPayment = offer.calculation?.leasingDownPayment ?? offer.leasingDownPayment;

  return {
    downPayment: isNotNil(downPayment) && Number(downPayment.amount) > 0 ? downPayment : null,
    monthlyServicesTotal:
      isNotNil(costs) && isNotEmpty(costs.monthlyServices) ? costs.monthlyServicesTotal : null,
    paidOnce: (costs?.oneTimeItems ?? []).flatMap((item) =>
      item.included
        ? []
        : [
            {
              id: item.kind,
              labelKey: getLeasingCostLabelKeys(item.kind).labelKey,
              tooltipKey: null,
              amount: item.amount,
              unit: null,
              decimals: 0,
            },
          ]
    ),
    conditional: CONDITIONAL_COST_FIELDS.flatMap((field) => {
      const amount = costs?.[field];
      const isPerKilometre = PER_KILOMETRE_FIELDS.includes(field);

      return isNil(amount)
        ? []
        : [
            {
              id: field,
              ...getLeasingCostLabelKeys(field),
              amount,
              unit: isPerKilometre ? ('PER_KILOMETRE' as const) : null,
              decimals: isPerKilometre ? getAmountScale(amount) : 0,
            },
          ];
    }),
    includes: isNil(costs)
      ? null
      : [
          ...costs.monthlyServices.map((service) => ({
            id: service.kind,
            ...getLeasingCostLabelKeys(service.kind),
            amount: service.amount,
            unit: 'PER_MONTH' as const,
            decimals: 0,
          })),
          ...costs.oneTimeItems
            .filter((item) => item.included)
            .map((item) => ({
              id: item.kind,
              labelKey: getLeasingCostLabelKeys(item.kind).labelKey,
              tooltipKey: INCLUDED_ITEMS_WITH_TOOLTIP.includes(item.kind)
                ? getLeasingCostLabelKeys(item.kind).tooltipKey
                : null,
              amount: item.amount,
              unit: null,
              decimals: 0,
            })),
        ],
  };
};
